import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren, inject } from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {fadeInOnEnterAnimation, fadeOutOnLeaveAnimation} from 'angular-animations';
import {Observable, Subscription, timer} from 'rxjs';
import {filter, finalize, map, switchMap, takeWhile} from 'rxjs/operators';

import {TimeService} from '@shared';
import {FeatureFlagService, SessionService} from '@core';

import {
    EmployeeService,
    JobIdInfo,
    NavigationService,
    OptimizationService,
    ProblemInstanceService,
    RouteKeyNumberComponent,
    RoutesResult,
    RoutesService,
    TotalKeyNumbers,
    TravelTimeTransportService
} from 'planner-shared';

import {JobConfigurationDelta} from '../../shared/models/job-configuration-delta.model';
import {RouteKeyNumberWeights} from '../../shared/models/route-key-number-weights.model';
import {TravelTimeTransport} from '../../shared/models/travel-time-transport';


@UntilDestroy()
@Component({
    selector: 'app-optimization-solution',
    templateUrl: './optimization-solution.component.html',
    styleUrls: ['./optimization-solution.component.scss'],
    animations: [fadeOutOnLeaveAnimation({ duration: 500 }), fadeInOnEnterAnimation({ duration: 500 })],
    standalone: false
})
export class OptimizationSolutionComponent implements OnInit {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly routesService = inject(RoutesService);
    private readonly sessionService = inject(SessionService);
    private readonly navigationService = inject(NavigationService);
    private readonly optimizationService = inject(OptimizationService);
    private readonly timeService = inject(TimeService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly employeeService = inject(EmployeeService);
    private readonly travelTimeTransportService = inject(TravelTimeTransportService);

    @Input() public jobIdInfo: JobIdInfo;
    @Input() public isRideSharingEnabled: boolean;
    @Input() public objectiveWeights$: Observable<{ [key: string]: string }>;
    @Output() public loading = new EventEmitter<string>();
    @Output() public optimizationComplete = new EventEmitter<boolean>();
    @Output() public solutionTick = new EventEmitter<boolean>();

    public configurationNames = ['travelTimeWeightProportion', 'timeWindowWeightProportion',
        'visitHistoryWeightProportion', 'overQualifiedWeightProportion',
        'patientTaskDurationAdjustmentPercentage',
        'useWorkBalance',
        'groupTasksByPatientAndSkillRequirement',
        'preferAssigningTasksToSameRosterDepartment'];

    public filteredDeltaConfigurations: JobConfigurationDelta[];
    public progress: number;
    public totalTime: number;
    public timeLeft: number;
    public loadingInfoText: string;
    public convergenceLikeliness: number;
    public totalKeyNumbers: TotalKeyNumbers;
    public routeKeyNumberWeights: RouteKeyNumberWeights;
    public rosterInInstance: boolean;
    public transportTravelTime: TravelTimeTransport;
    public transportTooltip: string;
    public mainSolution: boolean;
    private timerSubscription: Subscription;

    @ViewChildren(RouteKeyNumberComponent) private readonly routeKeyNumberComponents: QueryList<RouteKeyNumberComponent>;

    public filteredConfigurationDeltaList(): JobConfigurationDelta[] {
        return this.jobIdInfo.jobConfigurationDeltaList.filter((jobConfiguration: JobConfigurationDelta) => {
            return this.configurationNames.includes(jobConfiguration.optimizationConfigurationName);
        });
    }

    public updateFilteredDeltaConfigurationList(): void {
        this.filteredDeltaConfigurations = this.filteredConfigurationDeltaList();
    }

    ngOnInit(): void {
        this.initSolution();
        this.updateFilteredDeltaConfigurationList();

        this.problemInstanceService.rosterDepartmentIsPartOfInstance()
            .pipe()
            .subscribe(result => this.rosterInInstance = result.valueOf());
    }

    public select(): void {
        if (this.convergenceLikeliness === 1 || this.timeLeft === 0) {
            this.loading.emit('LOADING_INFO_STOPPING_OPTIMIZATION_SOLVER');
            this.optimizationService
                .stopOptimizationProcess()
                .pipe(
                    switchMap(() => this.routesService.setRoutes(this.jobIdInfo.jobId)),
                    finalize(() => this.loading.emit('LOADING_INFO_RETRIEVING_ROUTES'))
                )
                .subscribe(() => this.navigationService.navigateNext());
        }
    }

    public stopTimer(): void {
        this.convergenceLikeliness = 0;
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
            this.transportTravelTime = this.travelTimeTransportService.resetTransportTravelTime();
            this.transportTooltip = '';
        }
    }

    public initSolution(restart?: boolean): void {
        this.timeLeft = null;
        this.totalTime = null;
        this.totalKeyNumbers = null;
        this.convergenceLikeliness = null;
        this.problemInstanceService
            .getProblemInstance()
            .pipe(
                switchMap(result =>
                    this.routesService
                        .getRoutes(this.jobIdInfo.jobId)
                        .pipe(map(routesResult => ({ routesResult, problemInstance: result.problemInstance }))
                        )
                )
            )
            .subscribe(result => {
                const totalTime = result.problemInstance.officeInfo.solverRunTimeMinutes * 60;
                let timeLeft = 0;
                if (restart) {
                    timeLeft = totalTime;
                    this.sessionService.optimizationRunning = true;
                } else if (
                    result.routesResult.optimizationRunning &&
                    result.routesResult.secondsLeftOfOptimization > 0
                ) {
                    timeLeft = result.routesResult.secondsLeftOfOptimization;
                    this.sessionService.optimizationRunning = true;
                } else if (!result.routesResult.routes) {
                    timeLeft = totalTime;
                    this.sessionService.optimizationRunning = true;
                }
                this.initTimer(
                    timeLeft,
                    totalTime,
                    result.routesResult.convergenceLikeliness,
                    result.routesResult.routes?.totalKeyNumbers,
                    result.routesResult
                );
                if (this.isRideSharingEnabled && this.mainSolution) {
                    this.emitRideSharingPresence(result.routesResult);
                }
            });
        this.updateFilteredDeltaConfigurationList();
        this.routeKeyNumberWeights = <RouteKeyNumberWeights>{
            accurateArrivals: [0, 0 ],
            travelTime: [0, 0 ]
        };
        this.transportTravelTime = this.travelTimeTransportService.resetTransportTravelTime();
        this.transportTooltip = '';
        this.mainSolution = this.jobIdInfo.jobConfigurationDeltaList.length === 0;

    }
    private initTimer(
        timeLeft: number,
        totalTime: number,
        convergenceLikeliness: number,
        totalKeyNumbers?: TotalKeyNumbers,
        routesResult?: RoutesResult
    ): void {
        this.stopTimer();
        this.progress = 0;

        this.timeLeft = timeLeft;
        this.totalTime = totalTime;
        this.totalKeyNumbers = totalKeyNumbers;
        this.convergenceLikeliness = convergenceLikeliness;

        if (this.timeLeft === 0) {
            this.progress = 100;
            this.optimizationComplete.emit(true);
            this.loadWeights();
            this.travelTimeTransportService.mapToTravelTimeTransport(routesResult.routes.routeKeyNumbers)
                .subscribe(transportTravelTime => this.transportTravelTime = transportTravelTime);
            this.transportTooltip =
                this.travelTimeTransportService.formatTransportTooltip(this.transportTravelTime);
            return;
        }

        // timer for getting optimization info every 4 seconds
        this.timerSubscription = timer(1000, 4000)
            .pipe(
                untilDestroyed(this),
                takeWhile(() => this.timeLeft !== 0),
                switchMap(() => this.routesService.getRoutes(this.jobIdInfo.jobId)),
                filter(result => result.routes && result.routes.totalKeyNumbers != null),
            )
            .subscribe(result => {
                this.totalKeyNumbers = result.routes.totalKeyNumbers;
                this.timeLeft = result.secondsLeftOfOptimization;
                this.progress = Math.floor(100 - (this.timeLeft / this.totalTime) * 100);
                this.sessionService.optimizationRunning = this.progress !== 100;
                this.convergenceLikeliness = result.convergenceLikeliness;
                if (this.progress === 100) {
                    this.optimizationComplete.emit(true);
                }
                this.routeKeyNumberComponents.forEach(component => component.increase = null);

                this.loadWeights();
                this.transportTravelTime = this.travelTimeTransportService.resetTransportTravelTime();
                this.travelTimeTransportService.mapToTravelTimeTransport(result.routes.routeKeyNumbers)
                    .subscribe(transportTravelTime => this.transportTravelTime = transportTravelTime);
                this.transportTooltip =
                    this.travelTimeTransportService.formatTransportTooltip(this.transportTravelTime);
                if (this.isRideSharingEnabled && this.mainSolution) {
                    this.emitRideSharingPresence(result);
                }
            });
    }
    private emitRideSharingPresence(routesResult: RoutesResult) {
        if (routesResult.routes) {
            this.solutionTick
                .emit(routesResult.routes.tasks.some(task => task.rideSharingConfig));
        }
    }

    private loadWeights(): void {
        this.routeKeyNumberWeights.accurateArrivals[0] = this.timeService
            .extractHours(this.totalKeyNumbers.totalTimeBreakingTimeWindowsInSeconds);
        this.routeKeyNumberWeights.accurateArrivals[1]  = this.timeService
            .extractMinutes(this.totalKeyNumbers.totalTimeBreakingTimeWindowsInSeconds);
        this.routeKeyNumberWeights.travelTime[0] = this.timeService
            .extractHours(this.totalKeyNumbers.totalTravelTimeSeconds);
        this.routeKeyNumberWeights.travelTime[1] = this.timeService
            .extractMinutes(this.totalKeyNumbers.totalTravelTimeSeconds);
        this.routeKeyNumberWeights.visitContinuity
            = this.totalKeyNumbers.totalNumberOfPatientsVisitedByEmployeesNotInPrimaryGroup;
        this.routeKeyNumberWeights.correctCompetence
            = this.totalKeyNumbers.numberOfTasksWithOverqualifiedWork;
        this.routeKeyNumberWeights.unallocatedTasks
            = this.totalKeyNumbers.totalNumberOfNonAllocatedEmployeeTasks;
        this.routeKeyNumberWeights.differentRosterTasks
            = this.totalKeyNumbers.numberOfTasksWithDifferentRosterDepartment;
        this.routeKeyNumberWeights.overMaxEmployeeCount
            = this.totalKeyNumbers.numberOfPatientsVisitedByOverMaxEmployees;
        this.routeKeyNumberWeights.unallocatedTasksPatients
            = this.totalKeyNumbers.totalNumberOfNonAllocatedPatientTasks;
    }

    protected readonly String = String;
}
